import { useState } from "react";

import styles from "./CensusDrawer.module.css";
import { Formik, Form } from "formik";
import { SystemIcon, Button, Avatar } from "@justworkshr/milo-components";
import { getProspectUuid } from "utils/idHelpers";
import { ConvertToISODate } from "utils/helper";
import {
  GenderTypeEnum,
  ParticipantTypeEnum,
  useCreateDependentCensusesMutation,
  useUpdateDependentCensusesMutation,
} from "types/generated/operations";
import Tooltip from "uicomponents/Tooltip";
import Alert from "uicomponents/Alert";
import TextInput from "uicomponents/TextInput";
import RadioInput from "uicomponents/RadioInput";
import DateInput from "uicomponents/DateInput";
import SelectInput from "uicomponents/SelectInput";
import { HealthCensusType } from "uicomponents/CensusTable/types";

import { stateOption } from "pages/WorkersCompApplcation/utils/selectInputOptions";
import { dependentCensusValidationSchema } from "./utils/validationSchema";
import { Label } from "@justworkshr/milo-form";
import { SingleDependentType } from "types";

const stateCodeOptions = stateOption.map((state) => ({
  value: state.description,
  description: state.description,
}));

interface HealthCensusDrawerProps {
  isOpen: boolean;
  closeDrawer: (refetch?: boolean) => void;
  employeeData?: HealthCensusType;
  dependentData?: SingleDependentType | null;
}

const HealthCensusDrawer = ({
  employeeData,
  dependentData,
  isOpen,
  closeDrawer,
}: HealthCensusDrawerProps) => {
  const [createDependentCensuses, { loading: createDependentCensusesLoading }] =
    useCreateDependentCensusesMutation();

  const [updateDependentCensuses, { loading: updateDependentCensusesLoading }] =
    useUpdateDependentCensusesMutation();

  const [hasError, setError] = useState(false);

  const anyLoading =
    createDependentCensusesLoading || updateDependentCensusesLoading;

  const openClass = isOpen ? styles.open : "";

  let formattedDate = dependentData?.dateOfBirth || "";
  if (formattedDate) {
    const dateSubstrings = formattedDate.split("-");
    formattedDate = `${dateSubstrings[1]}/${dateSubstrings[2]}/${dateSubstrings[0]}`;
  }
  let formatedGender = dependentData?.gender?.toLowerCase() || "";
  if (formatedGender) {
    formatedGender =
      formatedGender.charAt(0).toUpperCase() + formatedGender.slice(1);
  }

  let defaultIsWaiving: string | null | boolean | undefined =
    employeeData?.healthInsInfo?.isWaiving;
  if (defaultIsWaiving !== undefined && defaultIsWaiving !== null) {
    defaultIsWaiving = defaultIsWaiving ? "Yes" : "No";
  }

  let defaultEmployeeType: any = employeeData?.isCompanyOwner;

  if (defaultEmployeeType !== undefined && defaultEmployeeType !== null) {
    defaultEmployeeType = defaultEmployeeType ? "owner" : "employee";
  }

  const defaults = {
    firstName: dependentData?.firstName || "",
    lastName: dependentData?.lastName || "",
    participantType: dependentData?.participantType || "",
    employeeType: defaultEmployeeType || "",
    isWaiving: defaultIsWaiving || "",
    homeZip: dependentData?.homeZip || "",
    state: dependentData?.homeState || "",
    dateOfBirth: formattedDate,
    gender: formatedGender,
  };

  const handleSubmission = async (values: any) => {
    const dependentUuid = dependentData?.uuid || "";

    try {
      const formattedDependent = {
        employeeId: employeeData?.uuid,
        gender: values.gender
          .toUpperCase()
          .replace(/-/g, "_") as GenderTypeEnum,
        firstName: values.firstName,
        lastName: values.lastName,
        zip: values.homeZip,
        state: values.state || null,
        participantType: values.participantType
          .toUpperCase()
          .replace(/-/g, "_") as ParticipantTypeEnum,
        dateOfBirth: ConvertToISODate(values.dateOfBirth),
      };
      if (!dependentUuid) {
        await createDependentCensuses({
          variables: {
            prospectId: getProspectUuid(),
            dependentCensuses: [formattedDependent],
          },
        });
      } else {
        await updateDependentCensuses({
          variables: {
            prospectId: getProspectUuid(),
            dependentCensuses: [{ ...formattedDependent, uuid: dependentUuid }],
          },
        });
      }

      closeDrawer(true);
    } catch {
      setError(true);
    }
  };

  const validationSchema = dependentCensusValidationSchema;

  const scrollToError = (fieldName: string) => {
    const element = document.querySelector(`[name="${fieldName}"]`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  return (
    <div className={`${styles.drawerWrapper} ${openClass}`}>
      <div className={styles.drawerContent}>
        <div className={styles.healthDrawerHeader}>
          <h1>
            {dependentData ? "Update" : "Add"} {employeeData?.firstName}'s
            Dependent
          </h1>
          <button
            className={styles.closeButton}
            onClick={() => closeDrawer()}
            aria-label="Close drawer"
            aria-roledescription="button"
          >
            <SystemIcon iconName="close" />
          </button>
        </div>
        <Formik
          initialValues={defaults}
          onSubmit={handleSubmission}
          validationSchema={validationSchema}
        >
          {({ errors, handleSubmit }) => (
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                const firstErrorFieldName = Object.keys(errors)[0];
                scrollToError(firstErrorFieldName);
                handleSubmit(e);
              }}
            >
              <div className={styles.drawerBody}>
                <div className={styles.healthDrawerEmployeeInfo}>
                  <Avatar
                    name={`${employeeData?.firstName} ${employeeData?.lastName}`}
                    size="md"
                  />
                  <div className={styles.healthEmployeeInfoRight}>
                    <h4>
                      {employeeData?.firstName} {employeeData?.lastName}
                    </h4>
                    <p>
                      {employeeData?.isCompanyOwner ? "Owner" : "Employee"}
                      {employeeData?.isCompanyOwner &&
                        (employeeData?.employeeInformation?.isPaid === "Paid"
                          ? " | Paid"
                          : " | Unpaid")}
                    </p>
                  </div>
                </div>
                <h4>Dependent Info</h4>
                <p>
                  These refers to information on most legal documents. The
                  carriers need it to provide the most accurate benefits.
                </p>
                <TextInput
                  label="Legal first name"
                  className={styles.drawerInput}
                  name="firstName"
                />
                <TextInput
                  label="Legal last name"
                  className={styles.drawerInput}
                  name="lastName"
                />

                <SelectInput
                  label="Relationship"
                  name="participantType"
                  placeholder="Select..."
                  options={[
                    {
                      value: "spouse",
                      description: "Spouse",
                    },
                    {
                      value: "child",
                      description: "Child",
                    },
                  ]}
                />

                <DateInput label="Date of birth" name="dateOfBirth" />
                <div className={styles.sexRadioGroup}>
                  <Tooltip tooltipText="Carriers limit us to these options, but we’re working to better reflect everyone.">
                    <Label className={styles.sexLabel}>
                      Sex
                      <span className={styles.sexInfoWrapper}>
                        <SystemIcon
                          iconName="info-circle"
                          size="extra-small"
                          className={styles.sexInfoIcon}
                        />
                      </span>
                    </Label>
                  </Tooltip>
                  <RadioInput
                    name="gender"
                    options={["Male", "Female"]}
                    label="Sex"
                    message="This refers to the sex on most legal documents. The carriers need it
                    to provide the most accurate benefits. It cannot be seen by anyone
                    at your company."
                  />
                </div>

                <TextInput
                  label="Zip"
                  className={`${styles.drawerInput} ${styles.halfInput}`}
                  name="homeZip"
                />

                <SelectInput
                  name="state"
                  className={`${styles.drawerInput} ${styles.halfInput}`}
                  options={stateCodeOptions}
                  label={"State"}
                  placeholder="Select..."
                />

                {hasError && (
                  <Alert message="Sorry, there was an error. Please try refreshing the page." />
                )}
                <Button
                  className={styles.saveHealthEmployeeButton}
                  color="brand"
                  loading={anyLoading}
                  type="submit"
                >
                  Save
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default HealthCensusDrawer;
